/**
 * Post Detail Sheet — bottom sheet with comments thread.
 * Includes quick comments, comment input, reply support.
 * DB triggers auto-send notifications on comment creation.
 */

import { useState, useCallback, useRef, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { X, Send, ArrowUp } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useComments, useCreateComment, useToggleCommentLike } from '@turnifly/shared/hooks'
import { QuickComments } from './quick-comments'
import { CommentList } from './comment-list'
import { Skeleton } from '@turnifly/shared/ui'

interface PostDetailSheetProps {
  postId: string
  postType: string
  onClose: () => void
}

export const PostDetailSheet = memo(function PostDetailSheet({
  postId,
  postType,
  onClose,
}: PostDetailSheetProps): React.ReactElement {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const userId = user?.id
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { data: comments, isLoading } = useComments(postId, userId ?? '')
  const createMutation = useCreateComment()
  const likeMutation = useToggleCommentLike()

  const [text, setText] = useState('')
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null)

  const handleSend = useCallback((): void => {
    const trimmed = text.trim()
    if (!userId || !trimmed) return

    createMutation.mutate(
      {
        postId,
        userId,
        text: trimmed,
        parentId: replyTo?.id ?? null,
      },
      {
        onSuccess: () => {
          setTimeout(() => {
            listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
          }, 300)
        },
      },
    )
    setText('')
    setReplyTo(null)
  }, [postId, userId, text, replyTo, createMutation])

  const handleQuickSend = useCallback((quickText: string): void => {
    if (!userId) return
    createMutation.mutate({
      postId,
      userId,
      text: quickText,
      isQuick: true,
    })
  }, [postId, userId, createMutation])

  const handleReply = useCallback((commentId: string, authorName: string): void => {
    setReplyTo({ id: commentId, name: authorName })
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const handleLikeComment = useCallback((commentId: string, isLiked: boolean): void => {
    if (!userId) return
    likeMutation.mutate({ commentId, userId, isLiked, postId })
  }, [postId, userId, likeMutation])

  const commentsCount = comments?.length ?? 0

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[51] bg-[var(--color-bg)] rounded-t-[28px] max-w-lg mx-auto flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 shrink-0">
          <div className="w-10 h-1 bg-white/[0.12] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-3 shrink-0">
          <h3 className="text-[16px] font-display font-bold text-text">
            {t('feed.comments.title')}
            {commentsCount > 0 && (
              <span className="text-text-hint/30 font-normal ml-1.5 text-[14px]">{commentsCount}</span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/[0.04] text-text-hint/50 tap-highlight-transparent hover:bg-white/[0.06] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Quick comments */}
        <div className="px-4 shrink-0 border-b border-white/[0.04] pb-2">
          <QuickComments postType={postType} onSend={handleQuickSend} />
        </div>

        {/* Comment list */}
        <div ref={listRef} className="flex-1 overflow-y-auto overscroll-y-contain min-h-0">
          {isLoading ? (
            <div className="px-4 space-y-4 py-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-2.5">
                  <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : commentsCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-[14px] text-text-hint/30">{t('feed.comments.empty')}</p>
              <p className="text-[12px] text-text-hint/20 mt-1">{t('feed.comments.beFirst')}</p>
            </div>
          ) : (
            <CommentList
              comments={comments ?? []}
              onReply={handleReply}
              onLikeComment={handleLikeComment}
            />
          )}
        </div>

        {/* Reply indicator */}
        {replyTo && (
          <div className="px-4 py-2 flex items-center gap-2 bg-white/[0.02] border-t border-white/[0.04] shrink-0">
            <ArrowUp size={12} className="text-primary/60 shrink-0" />
            <span className="text-[12px] text-text-hint/50 truncate flex-1">
              {t('feed.comments.replyingTo', { name: replyTo.name })}
            </span>
            <button
              onClick={() => setReplyTo(null)}
              className="tap-highlight-transparent shrink-0 p-1"
            >
              <X size={14} className="text-text-hint/40" />
            </button>
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 flex items-center gap-2.5 border-t border-white/[0.06] shrink-0 pb-safe">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-white/[0.06] shrink-0">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[11px] text-text-hint/50 font-semibold">
                {(user?.full_name ?? '?')[0]}
              </div>
            )}
          </div>

          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder={replyTo ? t('feed.comments.replyPlaceholder') : t('feed.comments.placeholder')}
            autoComplete="off"
            className="flex-1 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[13px] text-text placeholder:text-text-hint/30 focus:outline-none focus:border-primary/30 transition-colors"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || !userId || createMutation.isPending}
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all tap-highlight-transparent ${
              text.trim() && userId
                ? 'bg-primary text-white shadow-sm shadow-primary/20'
                : 'bg-white/[0.04] text-text-hint/20'
            }`}
          >
            {createMutation.isPending
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Send size={15} />
            }
          </button>
        </div>
      </motion.div>
    </>
  )
})
